import {
    ComponentInstanceWithConfig,
    ComponentModuleConfig,
    ComponentModuleViewConfig,
    ComponentsConfigs,
    ComponentSingletonsConfig,
    DeviceType,
    IModule,
    IService,
    IView,
    ModuleInitPhase
} from '@gongxigames/framework/core/types';
import {ComponentConfigManager} from '@gongxigames/framework/core/installer/ComponentConfigManager';
import {DependencyInjector} from '@gongxigames/framework/core/installer/DependencyInjector';
import {viewsComponentsSort} from '@gongxigames/framework/core/installer/viewsComponentsSort';
import {StrictEventEmitter} from '@gongxigames/framework/core/services/strictEventEmitter';
import {cacheAsSprite} from '@gongxigames/framework/core/fragments/display/CacheAsSprite';
import {Store} from '@gongxigames/framework/core';

export class Installer {
    private services: ComponentInstanceWithConfig<IService>[] = [];
    private singletons: ComponentInstanceWithConfig<IService, ComponentSingletonsConfig>[] = [];
    private modules: ComponentInstanceWithConfig<IModule, ComponentModuleConfig>[] = [];
    private views: ComponentInstanceWithConfig<IView, ComponentModuleViewConfig>[] = [];
    private componentConfigManager: ComponentConfigManager;
    public readonly dependencyInjector: DependencyInjector = new DependencyInjector();

    constructor(componentsConfig: ComponentsConfigs[]) {
        this.componentConfigManager = new ComponentConfigManager(componentsConfig);
    }

    public async installServices(): Promise<void> {
        this.services = this.dependencyInjector.bindComponents(this.componentConfigManager.getConfigsByType('services'));
        await this.callFunctionCreatedInComponents(this.services);
        this.componentConfigManager.getConfigsByType('classes').forEach(config => this.dependencyInjector.bindClass(config.NAME, config));
    }

    public async installSingletons(): Promise<void> {
        const classes = (this.componentConfigManager.getConfigsByType<ComponentSingletonsConfig>('singletons'))
            .filter(s => Boolean(s.isEnabled === undefined || s.isEnabled(this)));

        this.singletons = this.dependencyInjector.bindComponents(classes);
        await this.callFunctionCreatedInComponents(this.singletons);
    }

    public async initServices(): Promise<void> {
        await this.initComponents(this.services);
    }

    public installModules(): void {
        const configs = this.componentConfigManager.getModulesConfigs();
        const components = this.dependencyInjector.bindModulesComponents(configs);

        this.modules = components.modules;
        this.views = components.views;
    }

    public async initModules(initPhase: ModuleInitPhase, platform: DeviceType): Promise<void> {
        const modules = this.modules.filter(m => this.isTargetInitializationPhase(initPhase, m) && this.isEnabled(m.config, this, platform));
        const views = viewsComponentsSort(this.views.filter(v => this.isTargetInitializationPhase(initPhase, v) && this.isEnabled(v.config, this, platform)));

        await this.callFunctionCreatedInComponents(views);
        await this.callFunctionCreatedInComponents(modules);
        await this.initComponents(views);
        await this.initComponents(modules);
        modules.forEach(({instance}) => instance.restore());
    }

    public getModules(): IModule[] {
        return this.modules.map(({instance}) => instance);
    }

    public getEnabledModules(platform: DeviceType): IModule[] {
        return this.modules.filter(m => this.isEnabled(m.config, this, platform)).map(({instance}) => instance);
    }

    public getComponent<E>(id: symbol | string): E {
        return this.dependencyInjector.getComponent<E>(id);
    }

    public async destroyAllComponents(): Promise<void> {
        this.destroyComponents(this.services);
        this.destroyComponents(this.singletons);
        this.destroyComponents(this.views);
        this.destroyComponents(this.modules);
        this.dependencyInjector.unbindAll();
        StrictEventEmitter.clearAll();
        cacheAsSprite.clear();
        Store.resetStores();
    }

    private async initComponents(components: ComponentInstanceWithConfig[]): Promise<void> {
        for (const {instance} of components) {
            if (instance.init) {
                await instance.init();
            }
        }
    }

    private destroyComponents(components: ComponentInstanceWithConfig[]): void {
        components.forEach(({instance}) => instance.destroy && instance.destroy());
    }

    private async callFunctionCreatedInComponents(components: ComponentInstanceWithConfig[]): Promise<void> {
        for (const {instance, config} of components) {
            if (instance.configure) {
                await instance.configure(config);
            }
        }
    }

    private isEnabled<C extends ComponentModuleConfig>(config: C, parameters: any, platform: DeviceType): boolean {
        const isEnabled = Boolean(config.isEnabled === undefined || config.isEnabled(parameters));
        const isTargetPlatform = config.platform === undefined || config.platform === platform;

        return isEnabled && isTargetPlatform;
    }

    private isTargetInitializationPhase(initPhase: ModuleInitPhase, {config}: ComponentInstanceWithConfig<IModule | IView, ComponentModuleConfig>): boolean {
        return (config.initPhase === undefined && initPhase === 'default') || config.initPhase === initPhase;
    }
}
